import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Download, Gift, Plus, Trash2, Upload } from 'lucide-react';
import { SortSelect } from '@/shared/components/sort-select';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePassbook } from '@/shared/lib/passbook';
import { useAuth } from '@/shared/lib/auth';
import { canAccessTenantPath } from '@/shared/lib/navigation';
import { PAGE_HELP } from '@/shared/config/page-help';
import { useSelection } from '@/shared/lib/use-selection';
import { cn } from '@/shared/lib/cn';
import { PageHeader } from '@/shared/components/page-header';
import { FloatingActionBar } from '@/shared/components/floating-action-bar';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { MobileFiltersDialog, MobileFiltersTrigger } from '@/shared/components/mobile-filters';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Tooltip } from '@/shared/ui/tooltip';
import { Pagination } from '@/shared/ui/pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import type { Product, ProductRedemptionType } from '@/shared/types/product';
import { fetchProducts, saveProduct } from '../api/use-products';
import { compareProducts, matchesProductSearch, productPoints, scopeCatalog } from '../model/catalog-scope';
import { downloadProductTemplate } from '../model/product-csv';
import { ProductFormDialog } from './product-form-dialog';
import { ProductImportDialog } from './product-import-dialog';
import { ProductCard } from './product-card';

const PAGE_SIZE = 25;
const DEFAULT_SORT = 'name:asc';
const REDEMPTION_TYPES: ProductRedemptionType[] = ['birthday', 'courtesy', 'discount', 'product'];

export type ProductCatalogMode = 'product' | 'redemption';

function resolveRedeemType(value?: string | null): ProductRedemptionType {
  const normalized = value?.trim().toLowerCase();
  return REDEMPTION_TYPES.find((type) => type === normalized) ?? 'product';
}

const SORT_OPTIONS = [
  { value: 'name:asc', label: 'Nome A→Z' },
  { value: 'name:desc', label: 'Nome Z→A' },
  { value: 'value:desc', label: 'Maior valor' },
  { value: 'value:asc', label: 'Menor valor' },
];

interface ProductFiltersFieldsProps {
  valorMin: string;
  onValorMinChange: (value: string) => void;
  valorMax: string;
  onValorMaxChange: (value: string) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  stacked?: boolean;
}

function ProductFiltersFields({
  valorMin,
  onValorMinChange,
  valorMax,
  onValorMaxChange,
  sortValue,
  onSortChange,
  stacked = false,
}: ProductFiltersFieldsProps) {
  return (
    <div
      className={cn(
        'grid gap-2',
        stacked ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-[1fr_auto_1fr_minmax(10rem,auto)] sm:items-center',
      )}
    >
      <Input
        type="number"
        min={0}
        inputMode="numeric"
        placeholder="Valor mín."
        value={valorMin}
        onChange={(e) => onValorMinChange(e.target.value)}
        className="w-full"
      />
      <span
        className={cn(
          'text-center text-xs text-muted-foreground',
          stacked ? 'hidden' : 'hidden sm:inline',
        )}
      >
        até
      </span>
      <Input
        type="number"
        min={0}
        inputMode="numeric"
        placeholder="Valor máx."
        value={valorMax}
        onChange={(e) => onValorMaxChange(e.target.value)}
        className="w-full"
      />
      <SortSelect value={sortValue} onChange={onSortChange} options={SORT_OPTIONS} className="w-full" />
    </div>
  );
}

export function ProductsPage({ mode = 'product' }: { mode?: ProductCatalogMode }) {
  const isRedemption = mode === 'redemption';
  const { idPassbook, passbook } = usePassbook();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortValue, setSortValue] = useState(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  const [batchBusy, setBatchBusy] = useState(false);
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['products', idPassbook],
    queryFn: () => fetchProducts(idPassbook),
    staleTime: 60_000,
  });
  const showSkeleton = isLoading && data.length === 0;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const scoped = useMemo(
    () => scopeCatalog(data, isRedemption ? 'redemption' : 'product'),
    [data, isRedemption],
  );

  const filtered = useMemo(() => {
    let result = scoped.filter((product) => matchesProductSearch(product, debouncedSearch));

    const min = valorMin.trim() !== '' ? Number(valorMin) : undefined;
    const max = valorMax.trim() !== '' ? Number(valorMax) : undefined;
    if (min != null && Number.isFinite(min)) {
      result = result.filter((product) => productPoints(product) >= min);
    }
    if (max != null && Number.isFinite(max)) {
      result = result.filter((product) => productPoints(product) <= max);
    }

    result.sort((a, b) => compareProducts(sortValue, a, b));
    return result;
  }, [scoped, debouncedSearch, sortValue, valorMin, valorMax]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, sortValue, valorMin, valorMax]);

  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageIndex]);

  const selection = useSelection(pageRows);

  useEffect(() => {
    selection.clear();
  }, [pageIndex, debouncedSearch, valorMin, valorMax, sortValue]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (valorMin.trim()) n += 1;
    if (valorMax.trim()) n += 1;
    if (sortValue !== DEFAULT_SORT) n += 1;
    return n;
  }, [valorMin, valorMax, sortValue]);

  const clearFilters = useCallback(() => {
    setValorMin('');
    setValorMax('');
    setSortValue(DEFAULT_SORT);
  }, []);

  const openCreate = useCallback(() => {
    setEditProduct(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((product: Product) => {
    setEditProduct(product);
    setDialogOpen(true);
  }, []);

  const removeOne = useCallback(
    async (product: Product) => {
      const ok = await confirm({
        title: 'Remover produto',
        description: `Tem certeza que deseja remover “${product.name}”? Esta ação não pode ser desfeita.`,
        confirmLabel: 'Remover',
        variant: 'destructive',
      });
      if (!ok) return;
      try {
        await saveProduct({
          action: 'deleted',
          id: product._id,
          idPassbook,
          uniqueId: product.uniqueId,
          name: product.name,
          value: Number(product.value) || 0,
          redemption: Boolean(product.redemption),
          redemptionType: product.redemptionType ?? 'product',
          price: String(product.price ?? 0),
        });
        toast.success('Produto removido.');
        refetch();
        selection.clear();
      } catch {
        toast.error('Falha ao remover produto.');
      }
    },
    [idPassbook, refetch, selection, confirm],
  );

  const batchRemove = useCallback(async () => {
    if (selection.selected.length === 0) return;
    const ok2 = await confirm({
      title: 'Remover produtos',
      description: `Remover ${selection.selected.length} produto(s) selecionado(s)? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Remover',
      variant: 'destructive',
    });
    if (!ok2) return;
    setBatchBusy(true);
    let ok = 0;
    for (const product of selection.selected) {
      try {
        await saveProduct({
          action: 'deleted',
          id: product._id,
          idPassbook,
          uniqueId: product.uniqueId,
          name: product.name,
          value: Number(product.value) || 0,
          redemption: Boolean(product.redemption),
          redemptionType: product.redemptionType ?? 'product',
          price: String(product.price ?? 0),
        });
        ok += 1;
      } catch {
        /* continua */
      }
    }
    setBatchBusy(false);
    if (ok > 0) {
      toast.success(`${ok} produto${ok > 1 ? 's' : ''} removido${ok > 1 ? 's' : ''}.`);
      refetch();
      selection.clear();
    } else {
      toast.error('Falha ao remover produtos.');
    }
  }, [selection, idPassbook, refetch, confirm]);

  const batchSetRedemption = useCallback(
    async (redemption: boolean) => {
      if (selection.selected.length === 0) return;
      setBatchBusy(true);
      let ok = 0;
      for (const product of selection.selected) {
        if (Boolean(product.redemption) === redemption) continue;
        try {
          await saveProduct({
            action: 'edit',
            id: product._id,
            idPassbook,
            uniqueId: product.uniqueId,
            name: product.name,
            value: Number(product.value) || 0,
            redemption,
            redemptionType: product.redemptionType ?? 'product',
            price: String(product.price ?? 0),
          });
          ok += 1;
        } catch {
          /* continua */
        }
      }
      setBatchBusy(false);
      if (ok > 0) {
        toast.success(
          `${ok} produto${ok > 1 ? 's' : ''} ${redemption ? 'liberado' : 'bloqueado'}${ok > 1 ? 's' : ''} para resgate.`,
        );
        refetch();
        selection.clear();
      } else {
        toast.error('Nenhuma alteração realizada.');
      }
    },
    [selection, idPassbook, refetch],
  );

  const filterFieldsProps: ProductFiltersFieldsProps = {
    valorMin,
    onValorMinChange: setValorMin,
    valorMax,
    onValorMaxChange: setValorMax,
    sortValue,
    onSortChange: setSortValue,
  };

  const canManageProducts = Boolean(
    user && canAccessTenantPath(user.role, `/loja/${idPassbook}/produtos`, passbook, user.email),
  );
  if (!canManageProducts) {
    return (
      <>
        <PageHeader title={isRedemption ? 'Resgates' : 'Produtos'} help={PAGE_HELP.products} description="Catálogo indisponível para esta loja." />
        <p className="text-sm text-muted-foreground">Este passbook não possui módulo de produtos habilitado.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={isRedemption ? 'Resgates' : 'Produtos'}
        hideMetaOnMobile
        help={isRedemption ? PAGE_HELP.redemptions : PAGE_HELP.products}
        description={
          isRedemption
            ? `Benefícios com resgate — ${passbook.organizationName}`
            : `Catálogo — ${passbook.organizationName}`
        }
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" className="gap-1.5 sm:hidden">
                  Ações
                  <ChevronDown className="size-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="bottom" sideOffset={4} collisionPadding={12} className="min-w-[12.5rem]">
                <DropdownMenuItem onSelect={openCreate}>
                  <Plus />
                  {isRedemption ? 'Adicionar resgate' : 'Adicionar produto'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setImportOpen(true)}>
                  <Upload />
                  Importar CSV
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={downloadProductTemplate}>
                  <Download />
                  Baixar modelo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="hidden flex-wrap items-center gap-2 sm:flex">
              <Tooltip content="Baixar modelo CSV para preencher em lote" side="bottom">
                <Button variant="secondary" onClick={downloadProductTemplate}>
                  <Download className="size-4" /> Baixar modelo
                </Button>
              </Tooltip>
              <Tooltip content="Importar produtos a partir de um CSV" side="bottom">
                <Button variant="secondary" onClick={() => setImportOpen(true)}>
                  <Upload className="size-4" /> Importar CSV
                </Button>
              </Tooltip>
              <Tooltip content={isRedemption ? 'Cadastrar um benefício de resgate' : 'Cadastrar um produto sem resgate'} side="bottom">
                <Button onClick={openCreate}>
                  <Plus className="size-4" /> {isRedemption ? 'Adicionar resgate' : 'Adicionar produto'}
                </Button>
              </Tooltip>
            </div>
          </>
        }
      />

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome ou SKU…"
        toolbarExtra={
          <>
            <MobileFiltersTrigger activeCount={activeFilterCount} onClick={() => setFiltersOpen(true)} />
            <div className="hidden sm:block">
              <ProductFiltersFields {...filterFieldsProps} />
            </div>
          </>
        }
        selectAllId={isRedemption ? 'select-all-redemptions' : 'select-all-products'}
        itemCount={pageRows.length}
        allOnPageSelected={selection.allOnPageSelected}
        someOnPageSelected={selection.someOnPageSelected}
        onToggleAllOnPage={selection.toggleAllOnPage}
        isLoading={showSkeleton}
        isEmpty={!showSkeleton && filtered.length === 0}
        emptyTitle={scoped.length === 0 ? (isRedemption ? 'Nenhum resgate' : 'Nenhum produto') : 'Nenhum resultado'}
        emptyDescription={
          scoped.length === 0
            ? isRedemption
              ? 'Adicione o primeiro benefício de resgate.'
              : 'Adicione o primeiro produto do catálogo.'
            : 'Tente outro termo de busca.'
        }
        footer={
          filtered.length > PAGE_SIZE ? (
            <Pagination
              page={pageIndex}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPageIndex}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {pageRows.map((product, index) => (
          <li key={product._id || `sem-id-${index}`}>
            <ProductCard
              product={product}
              selected={selection.selectedIds.has(product._id)}
              onToggle={() => selection.toggle(product._id)}
              onEdit={() => openEdit(product)}
              onRemove={() => void removeOne(product)}
            />
          </li>
        ))}
      </SelectableListLayout>

      <MobileFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        activeCount={activeFilterCount}
        onClear={clearFilters}
        description="Filtre por faixa de valor e ordenação."
      >
        <ProductFiltersFields {...filterFieldsProps} stacked />
      </MobileFiltersDialog>

      <FloatingActionBar count={selection.count} onClear={selection.clear}>
        <Tooltip content="Permitir resgate no app para os selecionados" side="top">
          <Button size="sm" variant="secondary" disabled={batchBusy} onClick={() => batchSetRedemption(true)}>
            <Gift className="size-4" /> Habilitar resgate
          </Button>
        </Tooltip>
        <Tooltip content="Bloquear resgate no app para os selecionados" side="top">
          <Button size="sm" variant="secondary" disabled={batchBusy} onClick={() => batchSetRedemption(false)}>
            <Gift className="size-4" /> Desabilitar resgate
          </Button>
        </Tooltip>
        <Tooltip content="Excluir permanentemente os produtos selecionados" side="top">
          <Button size="sm" variant="destructive" disabled={batchBusy} onClick={batchRemove}>
            <Trash2 className="size-4" /> Remover
          </Button>
        </Tooltip>
      </FloatingActionBar>

      <ProductFormDialog
        idPassbook={idPassbook}
        product={editProduct}
        mode={mode}
        redeemType={resolveRedeemType(passbook.redeemType)}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => {
          refetch();
          selection.clear();
        }}
      />

      <ProductImportDialog
        idPassbook={idPassbook}
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          refetch();
          selection.clear();
        }}
      />
    </>
  );
}
